import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronLeft, Sparkles } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, EmptyState, HeroCard, LoadError, Pill } from "@/components/dryvn";
import { CollapsibleList, ListSearch, useListFilter } from "@/components/collapsible-list";
import {
  AddPanel,
  LifeField,
  LifeInput,
  LifeSelect,
  LifeTextarea,
  SubmitButton,
} from "@/components/life-forms";
// Screen migration (2026-07-05): journal via hook; moodLabel stays (pure
// helper). gratitudeAnniversary is computed from real entries below.
// 2026-07-15: web-entry form + honest empty/error states.
import { moodLabel, type JournalEntry } from "@/data/mock";
import { useAddJournalEntry, useElaborateJournalEntry, useJournal } from "@/lib/queries";

export const Route = createFileRoute("/more/journal")({
  head: () => ({
    meta: [
      { title: "Journal — Dryvn Life" },
      { name: "description", content: "Daily mood, gratitude, and a year of highlights." },
    ],
  }),
  component: JournalPage,
});

// BL-005 (operator 2026-07-19): tap an entry → elaborate. The card is a
// button; tapping opens an inline add-a-note editor; the addition lands on
// the entry with an org-clock time marker and everywhere the entry shows.
function JournalCard({ entry }: { entry: JournalEntry }) {
  const mood = moodLabel(entry.mood);
  const elaborate = useElaborateJournalEntry();
  const [open, setOpen] = useState(false);
  const [addition, setAddition] = useState("");
  const [failed, setFailed] = useState(false);

  const save = () => {
    const text = addition.trim();
    if (!text) return;
    setFailed(false);
    elaborate.mutate(
      { id: entry.id, addition: text },
      {
        onSuccess: () => {
          setAddition("");
          setOpen(false);
        },
        onError: () => setFailed(true),
      },
    );
  };

  return (
    <DCard>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="block w-full text-left"
        aria-expanded={open}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-xs" style={{ color: "var(--dryvn-grey)" }}>
            {entry.date}
            {entry.time ? ` · ${entry.time}` : ""}
          </span>
          <Pill tone="life">
            {mood.emoji} {mood.label} · {entry.mood}/10
          </Pill>
        </div>
        {entry.gratitude && (
          <div
            className="mb-2 rounded-xl border-l-2 px-3 py-2 font-display italic"
            style={{
              borderColor: "var(--dryvn-warm)",
              backgroundColor: "color-mix(in oklab, var(--dryvn-warm) 10%, transparent)",
              color: "var(--dryvn-ink)",
            }}
          >
            Grateful: {entry.gratitude}
          </div>
        )}
        <p
          className="whitespace-pre-line text-sm leading-relaxed"
          style={{ color: "var(--dryvn-ink)" }}
        >
          {entry.note}
        </p>
      </button>
      {open && (
        <form
          className="mt-3 space-y-2 border-t pt-3"
          style={{ borderColor: "color-mix(in oklab, var(--dryvn-grey) 25%, transparent)" }}
          onSubmit={(ev) => {
            ev.preventDefault();
            save();
          }}
        >
          <LifeTextarea
            rows={2}
            value={addition}
            onChange={(ev) => setAddition(ev.target.value)}
            placeholder="Add a detail or a note to this entry…"
          />
          <div className="flex items-center gap-3">
            <SubmitButton disabled={!addition.trim() || elaborate.isPending}>
              {elaborate.isPending ? "Saving…" : "Add note"}
            </SubmitButton>
            {failed && (
              <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                Couldn't save that — try again in a moment.
              </p>
            )}
          </div>
        </form>
      )}
    </DCard>
  );
}

function JournalPage() {
  const { data: journal, isError, error } = useJournal();
  const addEntry = useAddJournalEntry();
  const [showHighlights, setShowHighlights] = useState(false);

  // Optimistic local list seeded from the hook (shopping pattern): adds show
  // immediately in demo AND live; live refetch reseeds from the server.
  const [entries, setEntries] = useState<JournalEntry[]>(journal);
  useEffect(() => setEntries(journal), [journal]);

  const [note, setNote] = useState("");
  const [mood, setMood] = useState(7);
  const [kind, setKind] = useState<"journal" | "gratitude">("journal");
  const [submitError, setSubmitError] = useState(false);

  const submit = (close: () => void) => {
    const content = note.trim();
    if (!content) return;
    // Optimistic insert for instant feedback; snapshot so a failed save can
    // roll it back instead of leaving an entry that never persisted.
    const snapshot = entries;
    setEntries((prev) => [
      {
        id: crypto.randomUUID(),
        date: new Date().toISOString().slice(0, 10),
        mood,
        note: content,
        ...(kind === "gratitude" ? { gratitude: content } : {}),
      },
      ...prev,
    ]);
    setSubmitError(false);
    addEntry.mutate(
      {
        content,
        moodScore: mood,
        entryType: kind,
      },
      {
        onSuccess: () => {
          setNote("");
          setMood(7);
          setKind("journal");
          close();
        },
        onError: () => {
          setEntries(snapshot);
          setSubmitError(true);
        },
      },
    );
  };

  const {
    query: entryQuery,
    setQuery: setEntryQuery,
    results: visibleEntries,
    active: entrySearchActive,
  } = useListFilter(entries, (e) => [e.note, e.gratitude, e.date]);

  const gratitudeEntries = entries.filter((j) => j.gratitude);
  const oldest = entries.length > 0 ? entries[entries.length - 1]!.date : "";
  const gratitudeAnniversary = {
    entriesCount: gratitudeEntries.length,
    sinceLabel: oldest
      ? new Date(oldest).toLocaleDateString("en-CA", { month: "long", year: "numeric" })
      : "you started",
  };

  return (
    <AppShell>
      <Link
        to="/more"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-primary)" }}
      >
        <ChevronLeft className="h-4 w-4" /> More
      </Link>

      <HeroCard
        eyebrow="Journal"
        title={
          <>
            A{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              quiet
            </em>{" "}
            record.
          </>
        }
      />

      {/* Web entry — a second way in besides SMS. */}
      <div className="mt-6">
        <AddPanel label="Write an entry">
          {(close) => (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submit(close);
              }}
            >
              <LifeField label="How the day went">
                <LifeTextarea
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="A few words about today…"
                />
              </LifeField>
              <div className="grid grid-cols-2 gap-3">
                <LifeField label="Mood (1–10)">
                  <LifeSelect value={mood} onChange={(e) => setMood(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>
                        {n} · {moodLabel(n).emoji} {moodLabel(n).label}
                      </option>
                    ))}
                  </LifeSelect>
                </LifeField>
                <LifeField label="Kind">
                  <LifeSelect
                    value={kind}
                    onChange={(e) => setKind(e.target.value as "journal" | "gratitude")}
                  >
                    <option value="journal">Journal</option>
                    <option value="gratitude">Gratitude</option>
                  </LifeSelect>
                </LifeField>
              </div>
              <SubmitButton disabled={!note.trim()}>Save entry</SubmitButton>
              {submitError && (
                <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                  We couldn't save that just now — give it another try in a moment.
                </p>
              )}
            </form>
          )}
        </AddPanel>
      </div>

      {isError && (
        <div className="mt-6">
          <LoadError error={error} />
        </div>
      )}

      {!isError && entries.length === 0 && (
        <div className="mt-6">
          <EmptyState
            title="No journal entries yet. Jot one above, or text your Dryvn number and it lands here."
            smsExamples={["today was a good one — grateful for the sun", "mood 8, felt calm"]}
          />
        </div>
      )}

      {entries.length > 0 && (
        <>
          {/* Gratitude anniversary banner */}
          <div
            className="mt-6 flex flex-col gap-3 rounded-2xl p-5 sm:flex-row sm:items-center sm:justify-between"
            style={{ backgroundImage: "var(--grad-warm)" }}
          >
            <div className="flex items-start gap-3">
              <span
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full"
                style={{ backgroundColor: "color-mix(in oklab, white 60%, transparent)" }}
              >
                <Sparkles className="h-5 w-5" style={{ color: "var(--dryvn-warm-deep)" }} />
              </span>
              <div>
                <p className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                  A year of gratitude
                </p>
                <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                  {gratitudeAnniversary.entriesCount} entries since{" "}
                  {gratitudeAnniversary.sinceLabel}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowHighlights((v) => !v)}
              className="rounded-full px-4 py-2 text-sm font-medium text-white"
              style={{ backgroundColor: "var(--dryvn-primary)" }}
            >
              {showHighlights ? "Hide highlights" : "See highlights"}
            </button>
          </div>

          {showHighlights && gratitudeEntries.length > 0 && (
            <DCard className="mt-5">
              <ul className="space-y-4">
                {gratitudeEntries.slice(0, 5).map((g) => (
                  <li
                    key={g.id}
                    className="border-l-2 pl-4"
                    style={{ borderColor: "var(--dryvn-warm)" }}
                  >
                    <p
                      className="font-display text-lg italic leading-snug"
                      style={{ color: "var(--dryvn-ink)" }}
                    >
                      “{g.gratitude}”
                    </p>
                    <p
                      className="mt-1 font-mono text-[11px]"
                      style={{ color: "var(--dryvn-grey)" }}
                    >
                      {g.date}
                    </p>
                  </li>
                ))}
              </ul>
            </DCard>
          )}

          <div className="mt-8">
            <ListSearch
              value={entryQuery}
              onChange={setEntryQuery}
              placeholder="Search entries…"
              label="Search journal entries"
            />
            <CollapsibleList items={visibleEntries} resource="entries" filtered={entrySearchActive}>
              {(shown) => (
                <div className="space-y-3">
                  {shown.map((e) => (
                    <JournalCard key={e.id} entry={e} />
                  ))}
                </div>
              )}
            </CollapsibleList>
            {entrySearchActive && visibleEntries.length === 0 && (
              <DCard>
                <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                  No entries match that search.
                </p>
              </DCard>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
}
